import React, { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Trash2, MonitorSmartphone, Clock, Copy } from "lucide-react";
import PeopleLayout from "../../components/PeopleLayout";
import { peopleApi, formatDetail } from "../../lib/api";

/**
 * People Engine → Presence Devices
 *
 * Manager surface for the pairing contract:
 *   - select an outlet
 *   - generate a single-use pairing code (10-min TTL)
 *   - watch active codes count down
 *   - see paired devices, last_seen, label
 *   - revoke a paired device (Presence will fall back to its pair screen)
 *
 * No mention of Control Room. No payroll language. No admin/login wording.
 */
export default function PeopleDevices() {
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [activeCodes, setActiveCodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(Date.now());
  const [generatedCode, setGeneratedCode] = useState(null);

  // Tick every second so countdowns animate.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    peopleApi.get("/outlets").then((r) => {
      setOutlets(r.data);
      if (r.data.length && !outletId) setOutletId(r.data[0].id);
    }).catch((e) => setErr(formatDetail(e.response?.data?.detail)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    if (!outletId) return;
    try {
      const [c, d] = await Promise.all([
        peopleApi.get(`/people-engine/pairing/codes/active?outlet_id=${outletId}`),
        peopleApi.get(`/people-engine/devices?outlet_id=${outletId}`),
      ]);
      setActiveCodes(c.data);
      setDevices(d.data);
      setErr("");
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (!outletId) return;
    refresh();
    const i = setInterval(refresh, 8000);
    return () => clearInterval(i);
  }, [outletId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!outletId) return;
    setGenerating(true);
    setErr("");
    try {
      const r = await peopleApi.post("/people-engine/pairing/codes",
        { outlet_id: outletId, label: null });
      setGeneratedCode(r.data);
      await refresh();
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Could not generate code");
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (deviceId) => {
    setRevokingId(deviceId);
    try {
      await peopleApi.post(`/people-engine/devices/${deviceId}/revoke`, {});
      await refresh();
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Revoke failed");
    } finally {
      setRevokingId(null);
    }
  };

  const selectedOutletName = useMemo(
    () => outlets.find((o) => o.id === outletId)?.name || "",
    [outlets, outletId]
  );

  return (
    <PeopleLayout
      title="Presence Devices"
      subtitle="Pair, monitor and revoke Presence devices for each outlet"
      actions={
        <button
          data-testid="pe-devices-refresh"
          onClick={refresh}
          className="h-10 px-4 border border-neutral-700 hover:border-amber-400 hover:text-amber-300 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-6">
        <section className="col-span-12 lg:col-span-7 space-y-6">
          <div className="border border-neutral-800 bg-[#13161C] p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500">
              OUTLET
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <select
                data-testid="pe-devices-outlet"
                value={outletId}
                onChange={(e) => setOutletId(e.target.value)}
                className="h-12 px-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono"
              >
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <button
                onClick={generate}
                disabled={generating || !outletId}
                data-testid="pe-devices-generate"
                className="h-12 px-5 bg-amber-400 text-black font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Plus size={14} /> {generating ? "Generating…" : "Generate pairing code"}
              </button>
            </div>
            <p className="mt-3 text-xs font-mono text-neutral-500">
              Generate a pairing code for a Presence device at this outlet.
              Codes are single-use and expire in 10 minutes.
            </p>
          </div>

          {generatedCode && (
            <div
              data-testid="pe-devices-new-code"
              className="border border-amber-500 bg-amber-500/10 p-6"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
                NEW PAIRING CODE FOR {selectedOutletName.toUpperCase()}
              </div>
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <span
                  className="font-mono text-5xl tracking-[0.3em] font-black text-neutral-100 select-all"
                  data-testid="pe-devices-new-code-value"
                >
                  {generatedCode.code}
                </span>
                <button
                  onClick={() => {
                    try { navigator.clipboard?.writeText(generatedCode.code); } catch (_) {}
                  }}
                  className="h-10 px-3 border border-amber-300 text-amber-300 hover:bg-amber-400 hover:text-black font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
                >
                  <Copy size={12} /> Copy
                </button>
                <button
                  onClick={() => setGeneratedCode(null)}
                  className="h-10 px-3 border border-neutral-700 hover:border-neutral-500 text-neutral-300 font-mono text-xs uppercase tracking-wider"
                >
                  Dismiss
                </button>
              </div>
              <p className="mt-3 text-xs font-mono text-neutral-300">
                Type this code into the Presence device&apos;s pairing screen.
              </p>
            </div>
          )}

          <div className="border border-neutral-800 bg-[#13161C]">
            <div className="px-5 py-3 border-b border-neutral-800 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-400">
                Active codes
              </div>
              <span className="font-mono text-[11px] text-neutral-500">{activeCodes.length}</span>
            </div>
            {activeCodes.length === 0 ? (
              <div className="p-8 text-center font-mono text-xs text-neutral-500" data-testid="pe-devices-codes-empty">
                No active codes for this outlet.
              </div>
            ) : (
              <ul>
                {activeCodes.map((c) => {
                  const remainingMs = Math.max(0, new Date(c.expires_at_iso).getTime() - now);
                  const min = Math.floor(remainingMs / 60000);
                  const sec = Math.floor((remainingMs % 60000) / 1000);
                  return (
                    <li
                      key={c.code}
                      className="px-5 py-4 border-t border-neutral-800 flex items-center justify-between"
                      data-testid={`pe-devices-code-${c.code}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-2xl tracking-[0.25em] font-bold text-amber-300">
                          {c.code}
                        </span>
                        {c.label && (
                          <span className="font-mono text-[11px] text-neutral-400">{c.label}</span>
                        )}
                      </div>
                      <span className="font-mono text-[11px] text-neutral-400 inline-flex items-center gap-1">
                        <Clock size={12} /> {min}m {sec.toString().padStart(2, "0")}s
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="col-span-12 lg:col-span-5">
          <div className="border border-neutral-800 bg-[#13161C]">
            <div className="px-5 py-3 border-b border-neutral-800 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-400 inline-flex items-center gap-2">
                <MonitorSmartphone size={14} /> Paired devices
              </div>
              <span className="font-mono text-[11px] text-neutral-500">{devices.length}</span>
            </div>
            {devices.length === 0 ? (
              <div className="p-8 text-center font-mono text-xs text-neutral-500" data-testid="pe-devices-paired-empty">
                No devices paired to this outlet.
              </div>
            ) : (
              <ul>
                {devices.map((d) => (
                  <li
                    key={d.id}
                    className="px-5 py-4 border-t border-neutral-800"
                    data-testid={`pe-devices-paired-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-mono text-sm text-neutral-100" data-testid={`pe-devices-paired-${d.id}-label`}>
                          {d.device_name || "Presence"}
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-neutral-500">
                          paired {d.paired_at_iso
                            ? new Date(d.paired_at_iso).toLocaleString("en-GB")
                            : "—"}
                        </div>
                        <div className="font-mono text-[10px] text-neutral-500">
                          last seen {d.last_seen_at_iso
                            ? new Date(d.last_seen_at_iso).toLocaleString("en-GB")
                            : "—"}
                        </div>
                        <div className="font-mono text-[10px] text-neutral-500">
                          outlet · {d.outlet_name}
                        </div>
                      </div>
                      <button
                        onClick={() => revoke(d.id)}
                        disabled={revokingId === d.id}
                        data-testid={`pe-devices-revoke-${d.id}`}
                        className="h-8 px-3 border border-red-500/60 text-red-400 hover:bg-red-500/10 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Trash2 size={11} /> {revokingId === d.id ? "…" : "Revoke"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
      {err && (
        <div className="mt-4 text-red-400 font-mono text-sm" data-testid="pe-devices-error">{err}</div>
      )}
    </PeopleLayout>
  );
}
